import Link from 'next/link';
import CategoryLabel from '../category/CategoryLabel';
import Paragraph from '../common/Paragraph';
import DateLabel from '../date/DateLabel';
import WysiwygBlock from '../blocks/WysiwygBlock';
import TitleH3 from '../common/TitleH3';
import DirectusImage from '../image/DirectusImage';
import { CalendarCardProps, CardProps, PlayerCardProps } from './types';
import { PlayerCard } from './CardPlayer';
import CardPush from './CardPush';
import CardAction from './CardAction';
import CardFaq from './CardFaq';
import ImageCard from './CardImage';
import SocialCard from './CardSocial';
import CardCalendarOrScore from './CardCalendarOrScore';
import PlayVideoIcon from '../../../../public/svg/play_video.svg';
import GalleryIcon from '../../../../public/png/photo_group_icon.png';
import PageCard from './CardPage';

function DefaultCard({
  callToActionUrl: { url, external, slug },
  category,
  description,
  displayDescription = true,
  duration,
  image,
  imageAlt,
  imageBlur,
  imageHeight,
  imageSizes,
  isoDate,
  dateLabelType = 'default',
  grayBackground,
  photoCount = 0,
  title,
  titleUppercase = true,
  titleBold = false,
  titleBlockStyles = '',
}: CardProps) {
  const link = external ? url : `${url}/${slug}`;
  const target = external ? '_blank' : '_self';
  let height = imageHeight ?? 'pb-2/3';
  let imageSizesValue = imageSizes ?? '(min-width: 66em) 30vw, (min-width: 44em) 40vw, 100vw';
  let titleSize: 'default' | 'small' | 'medium' | 'big' = image ? 'default' : 'big';
  if (titleBlockStyles) {
    titleSize = titleBlockStyles as 'default' | 'small' | 'medium' | 'big';
    if (!image) {
      titleSize = 'big';
      titleUppercase = true;
    }
  }

  return (
    <div
      className={`card card-compact min-h-[200px] w-full shadow-none ${grayBackground} ${!image ? 'card-gradient' : ''}`}
    >
      <Link href={link} target={target}>
        {image && (
          <figure className={`relative ${height} rounded-md`}>
            <DirectusImage
              src={image || ''}
              alt={imageAlt || ''}
              blur={imageBlur}
              dimension={{ sizes: imageSizesValue }}
              styles="absolute"
            />
            {duration && (
              <div className="absolute bottom-[20px] right-[20px] flex max-w-max items-center gap-2.5">
                <DirectusImage
                  src={PlayVideoIcon}
                  alt="Visionner la galerie video"
                  dimension={{ width: 20, height: 20 }}
                />
                <span className="rounded bg-white p-[5px] text-xs font-semibold uppercase text-gray-400 backdrop-blur-sm">
                  {duration}
                </span>
              </div>
            )}
            {photoCount > 0 && (
              <div className="absolute bottom-[20px] right-[20px] flex max-w-max items-center gap-2.5">
                <span className="flex items-center gap-1 rounded bg-white p-[5px] text-xs font-semibold text-gray-400 backdrop-blur-sm">
                  <DirectusImage
                    src={GalleryIcon}
                    alt="Ouvrir la galerie photo"
                    dimension={{ width: 10.5, height: 10.25 }}
                  />
                  {photoCount} photos
                </span>
              </div>
            )}
          </figure>
        )}
        <div className="card-body gap-4 override-card-body lg:py-5">
          {category && (
            <CategoryLabel category={category} styles={`${!image ? 'mt-[25px]' : ''}`} />
          )}
          <TitleH3
            size={titleSize}
            color="black"
            titleUpperCase={titleUppercase}
            titleBold={titleBold}
            titleTracking="tracking-wide"
            heading={false}
            customFont={image ? false : true}
            styles="line-clamp-2 mr-8"
          >
            {title}
          </TitleH3>
          {displayDescription && description && (
            <div className="line-clamp-4 grow">
              <WysiwygBlock html={description || ''} />
            </div>
          )}
          {isoDate && <DateLabel isoDate={isoDate} type={dateLabelType} styles="text-xs" />}
        </div>
      </Link>
    </div>
  );
}

function SideCard({
  title,
  image,
  imageAlt,
  description,
  displayDescription = true,
  imageBlur,
}: CardProps) {
  return (
    <div className="card shadow-xl lg:card-side">
      <figure className="relative flex min-h-96 w-5/12">
        <DirectusImage
          src={image || ''}
          alt={imageAlt || ''}
          blur={imageBlur}
          dimension={{ sizes: '100vw' }}
        />
      </figure>
      <div className="w-7/12 p-5">
        <h2 className="card-title">{title}</h2>
        {displayDescription && <Paragraph>{description}</Paragraph>}
        <div className="card-actions justify-end">
          <button className="btn btn-primary">Listen</button>
        </div>
      </div>
    </div>
  );
}

export default function Card(props: CardProps | CalendarCardProps | PlayerCardProps) {
  switch (props.type) {
    case 'action':
      return <CardAction {...props} />;
    case 'calendar':
      return <CardCalendarOrScore {...(props as CalendarCardProps)} />;
    case 'faq':
      return <CardFaq {...props} />;
    case 'image':
      return <ImageCard {...props} />;
    case 'page':
      return <PageCard {...props} />;
    case 'push':
      return <CardPush {...props} />;
    case 'side':
      return <SideCard {...props} />;
    case 'team':
      return <PlayerCard {...(props as PlayerCardProps)} />;
    case 'social':
      return <SocialCard {...props} />;
    default:
      return <DefaultCard {...props} />;
  }
}
